import { BrandRecord, KeywordBrandRecord, KeywordRecord } from '../../../zustandStores/BrandTrafficStore';

/**
 * Gets the top N brands by traffic volume
 * @param brands Array of brand records
 * @param count Number of brands to return
 * @returns Top brands by traffic
 */
export const getTopBrandsByVolume = (brands: BrandRecord[], count: number = 5): BrandRecord[] => {
  return [...brands].sort((a, b) => b.value - a.value).slice(0, count);
};

/**
 * Interface for the grouped keyword data by brand
 */
export interface GroupedBrandKeywordData {
  brandId: number;
  brandName: string;
  keywordOrder: string[];
  keywordVolume: number[];
  totalTraffic: number;
}

/**
 * Interface for entity records (categories, subcategories, topics)
 */
export interface EntityRecord {
  fieldKey: string | number;
  value: number;
  displayName?: string;
}

/**
 * Interface for entity-brand records from the API
 */
export interface EntityBrandRecord {
  fieldKey: string | number;
  brandId: number;
  value: number;
}

/**
 * Groups keyword data by brand and creates arrays of keywords and their volumes
 * @param brands Array of brand records
 * @param keywordBrandData Array of keyword-brand records
 * @param keywords Array of total keyword records to determine global ordering
 * @param brandIds Optional array of brand IDs to filter by (if not provided, all brands will be included)
 * @param keywordLimit Optional limit for the number of keywords per brand
 * @returns Array of grouped brand-keyword data
 */
export const groupKeywordDataByBrand = (
  brands: BrandRecord[],
  keywordBrandData: KeywordBrandRecord[],
  keywords: KeywordRecord[],
  brandIds?: number[],
  keywordLimit: number = 10
): GroupedBrandKeywordData[] => {
  const filteredBrands = brandIds ? brands.filter((brand) => brandIds.includes(brand.fieldKey)) : brands;

  // First, determine the global keyword order based on total volume
  const topKeywords = [...keywords]
    .sort((a, b) => b.value - a.value)
    .slice(0, keywordLimit)
    .map((kw) => kw.fieldKey);

  return filteredBrands.map((brand) => {
    // Get all keywords for this brand (not sorted yet)
    const brandKeywordsMap = new Map<string, number>();

    // Create a map of keyword to volume for this brand
    keywordBrandData
      .filter((item) => item.brandId === brand.fieldKey)
      .forEach((item) => {
        brandKeywordsMap.set(item.fieldKey, item.value);
      });

    // Create arrays maintaining the global keyword order
    const keywordOrder: string[] = [];
    const keywordVolume: number[] = [];
    let totalTraffic = 0;

    // Use the global top keywords order
    topKeywords.forEach((keyword) => {
      const volume = brandKeywordsMap.get(keyword) || 0;
      keywordOrder.push(keyword);
      keywordVolume.push(volume);
      totalTraffic += volume;
    });

    return {
      brandId: brand.fieldKey,
      brandName: brand.metadata.brandName,
      keywordOrder,
      keywordVolume,
      totalTraffic
    };
  });
};

/**
 * Groups entity data (categories, subcategories, topics) by brand
 * @param brands Array of brand records
 * @param entityBrandData Array of entity-brand records
 * @param entities Array of entity records to determine global ordering
 * @param brandIds Optional array of brand IDs to filter by
 * @param entityLimit Optional limit for the number of entities
 * @returns Array of grouped brand-entity data in the same format as groupKeywordDataByBrand
 */
export const groupEntityDataByBrand = (
  brands: BrandRecord[],
  entityBrandData: EntityBrandRecord[],
  entities: EntityRecord[],
  brandIds?: number[],
  entityLimit: number = 20
): GroupedBrandKeywordData[] => {
  const filteredBrands = brandIds ? brands.filter((brand) => brandIds.includes(brand.fieldKey)) : brands;

  // Determine the global entity order based on total volume
  const topEntities = [...entities]
    .sort((a, b) => (b.value as number) - (a.value as number))
    .slice(0, entityLimit)
    .map((entity) => String(entity.fieldKey));

  return filteredBrands.map((brand) => {
    const brandEntitiesMap = new Map<string, number>();

    entityBrandData
      .filter((item) => item.brandId === brand.fieldKey)
      .forEach((item) => {
        brandEntitiesMap.set(String(item.fieldKey), item.value as number);
      });

    const entityOrder: string[] = [];
    const entityVolume: number[] = [];
    let totalTraffic = 0;

    topEntities.forEach((entity) => {
      const volume = brandEntitiesMap.get(entity) || 0;
      entityOrder.push(entity);
      entityVolume.push(volume);
      totalTraffic += volume;
    });

    return {
      brandId: brand.fieldKey,
      brandName: brand.metadata.brandName,
      keywordOrder: entityOrder,
      keywordVolume: entityVolume,
      totalTraffic
    };
  });
};
